import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  Alert,
  Avatar,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { authService } from '@/api/services';
import { useAuthStore } from '@/store';
import { safeReturnTo } from '@/utils/loginRedirect';

const CODE_LENGTH = 6;
type Step = 'email' | 'code' | 'passkey';

export const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const { setAuthTokens } = useAuthStore();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [devLoginAvailable, setDevLoginAvailable] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Best-effort probe: prod returns error; local with AUTH_DEV_LOGIN shows button after first attempt is fine.
    setDevLoginAvailable(import.meta.env.DEV);
  }, []);

  useEffect(() => {
    if (step !== 'email' || !browserSupportsWebAuthn()) return;
    let cancelled = false;
    (async () => {
      try {
        if (!(await browserSupportsWebAuthnAutofill())) return;
        const begin = await authService.webauthnLoginBegin({});
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        const assertion = await startAuthentication({
          optionsJSON: begin.publicKey as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
          useBrowserAutofill: true,
        });
        if (cancelled) return;
        setBusy(true);
        const tokens = await authService.webauthnLoginFinish({
          challengeId: begin.challengeId,
          credential: assertion as unknown as Record<string, unknown>,
        });
        setAuthTokens(tokens.accessToken, tokens.refreshToken);
        navigate(returnTo);
      } catch {
        // User dismissed conditional UI or no credential — ignore.
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [step, navigate, returnTo, setAuthTokens]);

  const finishWithTokens = (accessToken: string, refreshToken: string, offerPasskey: boolean) => {
    setAuthTokens(accessToken, refreshToken);
    if (offerPasskey && browserSupportsWebAuthn()) {
      setStep('passkey');
      return;
    }
    navigate(returnTo);
  };

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await authService.emailStart({ email: email.trim() });
      setCode('');
      setStep('code');
    } catch {
      setError('Не удалось отправить код. Попробуйте позже.');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== CODE_LENGTH) return;
    setError('');
    setBusy(true);
    try {
      const res = await authService.emailConfirm({ email: email.trim(), code });
      finishWithTokens(res.accessToken, res.refreshToken, true);
    } catch {
      setError('Неверный или просроченный код');
    } finally {
      setBusy(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError('');
    setBusy(true);
    try {
      const begin = await authService.webauthnLoginBegin(
        email.trim() ? { email: email.trim() } : {},
      );
      const assertion = await startAuthentication({
        optionsJSON: begin.publicKey as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });
      const tokens = await authService.webauthnLoginFinish({
        challengeId: begin.challengeId,
        credential: assertion as unknown as Record<string, unknown>,
      });
      finishWithTokens(tokens.accessToken, tokens.refreshToken, false);
    } catch {
      setError('Не удалось войти с Passkey');
    } finally {
      setBusy(false);
    }
  };

  const handleRegisterPasskey = async () => {
    setError('');
    setBusy(true);
    try {
      const begin = await authService.webauthnRegisterBegin();
      const attestation = await startRegistration({
        optionsJSON: begin.publicKey as unknown as Parameters<typeof startRegistration>[0]['optionsJSON'],
      });
      await authService.webauthnRegisterFinish({
        challengeId: begin.challengeId,
        credential: attestation as unknown as Record<string, unknown>,
      });
      navigate(returnTo);
    } catch {
      setError('Не удалось сохранить Passkey');
    } finally {
      setBusy(false);
    }
  };

  const handleDevLogin = async () => {
    setError('');
    setBusy(true);
    try {
      const tokens = await authService.devLogin({ email: email.trim() });
      finishWithTokens(tokens.accessToken, tokens.refreshToken, false);
    } catch {
      setError('Dev login недоступен');
      setDevLoginAvailable(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar
            src="/logo-v4.png"
            alt="4EMP"
            variant="square"
            sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'transparent' }}
          />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            4EMP
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {step === 'email' && 'Вход или регистрация'}
            {step === 'code' && 'Код из письма'}
            {step === 'passkey' && 'Быстрый вход'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 'email' && (
          <form onSubmit={handleEmailContinue}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              name="email"
              autoComplete="username webauthn"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoFocus
            />
            <Button type="submit" fullWidth variant="contained" size="large" sx={{ mt: 2 }} disabled={busy}>
              {busy ? 'Отправка...' : 'Продолжить'}
            </Button>
            {browserSupportsWebAuthn() && (
              <Button fullWidth variant="outlined" size="large" sx={{ mt: 1.5 }} onClick={handlePasskeyLogin} disabled={busy}>
                Войти с Passkey
              </Button>
            )}
            {devLoginAvailable && (
              <Button fullWidth size="large" sx={{ mt: 1 }} onClick={handleDevLogin} disabled={busy || !email.trim()}>
                Dev login
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, textAlign: 'center' }}>
              Нажимая «Продолжить», вы принимаете{' '}
              <Link to="/terms" style={{ color: 'inherit' }}>
                условия использования
              </Link>
              .
            </Typography>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={handleConfirmCode}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Код отправлен на {email}
            </Typography>
            <TextField
              fullWidth
              label="Код из письма"
              name="one-time-code"
              id="one-time-code"
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
              margin="normal"
              required
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: CODE_LENGTH,
                  autoComplete: 'one-time-code',
                  'aria-label': 'Код из письма',
                },
              }}
              sx={{
                mb: 1,
                '& input': { letterSpacing: '0.4em', fontSize: 24, textAlign: 'center' },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={busy || code.length !== CODE_LENGTH}
            >
              {busy ? 'Проверка...' : 'Войти'}
            </Button>
            <Button fullWidth sx={{ mt: 1 }} onClick={() => setStep('email')} disabled={busy}>
              Другой email
            </Button>
          </form>
        )}

        {step === 'passkey' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Сохраните Passkey для быстрого входа на этом устройстве.
            </Typography>
            <Button fullWidth variant="contained" size="large" onClick={handleRegisterPasskey} disabled={busy}>
              Сохранить Passkey
            </Button>
            <Button fullWidth sx={{ mt: 1.5 }} onClick={() => navigate(returnTo)} disabled={busy}>
              Пропустить
            </Button>
          </Box>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 3 }}>
        © 2026 4EMP
      </Typography>
    </Box>
  );
};
